<script setup lang="ts">
import { NButton, NInput } from 'naive-ui'

// One repeated section of the CV editor (roles, education, skills, …):
// a list of entries with string fields and, optionally, one multi-line
// list field edited one item per line. Reordering and removal are the
// small hand edits the owner makes before approving; anything bigger is a
// regeneration.
import type { EditorEntry, EditorField } from './cvEditorTypes'

const props = defineProps<{
  title: string
  testid: string
  entries: EditorEntry[]
  fields: EditorField[]
  listField?: EditorField
  blank: () => EditorEntry
}>()

const emit = defineEmits<{ 'update:entries': [entries: EditorEntry[]] }>()

function setField(index: number, key: string, value: string): void {
  const next = props.entries.map((e, i) => (i === index ? { ...e, [key]: value } : e))
  emit('update:entries', next)
}

function setList(index: number, key: string, text: string): void {
  const items = text
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
  const next = props.entries.map((e, i) => (i === index ? { ...e, [key]: items } : e))
  emit('update:entries', next)
}

function listText(entry: EditorEntry, key: string): string {
  const value = entry[key]
  return Array.isArray(value) ? value.join('\n') : ''
}

function stringValue(entry: EditorEntry, key: string): string {
  const value = entry[key]
  return typeof value === 'string' ? value : ''
}

function add(): void {
  emit('update:entries', [...props.entries, props.blank()])
}

function remove(index: number): void {
  emit(
    'update:entries',
    props.entries.filter((_, i) => i !== index),
  )
}

function move(index: number, delta: number): void {
  const target = index + delta
  if (target < 0 || target >= props.entries.length) return
  const next = [...props.entries]
  ;[next[index], next[target]] = [next[target], next[index]]
  emit('update:entries', next)
}
</script>

<template>
  <section class="flex flex-col gap-2" :data-testid="testid">
    <div class="flex items-center justify-between">
      <h3 class="text-xs uppercase tracking-wide text-white/40 m-0">{{ title }}</h3>
      <NButton size="tiny" tertiary :data-testid="`${testid}-add`" @click="add">Add</NButton>
    </div>
    <div
      v-for="(entry, i) in entries"
      :key="i"
      class="rounded border border-white/10 p-3 flex flex-col gap-2"
      :data-testid="`${testid}-${i}`"
    >
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label v-for="field in fields" :key="field.key" class="flex flex-col gap-1 text-xs text-white/60">
          {{ field.label }}
          <NInput
            :value="stringValue(entry, field.key)"
            :placeholder="field.placeholder"
            size="small"
            :data-testid="`${testid}-${i}-${field.key}`"
            @update:value="(v: string) => setField(i, field.key, v)"
          />
        </label>
      </div>
      <label v-if="listField" class="flex flex-col gap-1 text-xs text-white/60">
        {{ listField.label }}
        <NInput
          type="textarea"
          :autosize="{ minRows: 2 }"
          :value="listText(entry, listField.key)"
          :placeholder="listField.placeholder"
          size="small"
          :data-testid="`${testid}-${i}-${listField.key}`"
          @update:value="(v: string) => setList(i, listField!.key, v)"
        />
      </label>
      <div class="flex gap-2">
        <NButton size="tiny" tertiary :disabled="i === 0" :data-testid="`${testid}-${i}-up`" @click="move(i, -1)"
          >↑</NButton
        >
        <NButton
          size="tiny"
          tertiary
          :disabled="i === entries.length - 1"
          :data-testid="`${testid}-${i}-down`"
          @click="move(i, 1)"
          >↓</NButton
        >
        <NButton size="tiny" tertiary type="error" :data-testid="`${testid}-${i}-remove`" @click="remove(i)">
          Remove
        </NButton>
      </div>
    </div>
  </section>
</template>
